/**
 * Generated by orval 🍺
 * Do not edit manually.
 * SolidOps API
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  IncidentCustomerOptionsPageResponseOutput,
  IncidentDetailResponseOutput,
  IncidentPageResponseOutput,
} from '../model';
import type {
  IncidentCustomerCommand,
  IncidentErrorResponse,
  IncidentPriorityCommand,
  IncidentStatusCommand,
  IncidentTeamCommand,
  ListIncidentCustomerOptionsParams,
  ListIncidentsParams,
} from '../model';

import { validatedIncidentFetch } from '../../incidents.transport';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getListIncidentCustomerOptionsUrl = (
  incidentId: string,
  params: ListIncidentCustomerOptionsParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/incidents/${incidentId}/customer-options?${stringifiedParams}`
    : `/api/v1/incidents/${incidentId}/customer-options`;
};

export const listIncidentCustomerOptions = async (
  incidentId: string,
  params: ListIncidentCustomerOptionsParams,
  options?: Parameters<typeof validatedIncidentFetch>[1],
): Promise<IncidentCustomerOptionsPageResponseOutput> => {
  return validatedIncidentFetch<IncidentCustomerOptionsPageResponseOutput>(
    getListIncidentCustomerOptionsUrl(incidentId, params),
    {
      ...options,
      method: 'GET',

      schema: IncidentCustomerOptionsPageResponseOutput,
    },
  );
};

export const getListIncidentCustomerOptionsQueryKey = (
  incidentId: string,
  params?: ListIncidentCustomerOptionsParams,
) => {
  return [`/api/v1/incidents/${incidentId}/customer-options`, ...(params ? [params] : [])] as const;
};

export const getListIncidentCustomerOptionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listIncidentCustomerOptions>>,
  TError = IncidentErrorResponse,
>(
  incidentId: string,
  params: ListIncidentCustomerOptionsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listIncidentCustomerOptions>>, TError, TData>;
    request?: SecondParameter<typeof validatedIncidentFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListIncidentCustomerOptionsQueryKey(incidentId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listIncidentCustomerOptions>>> = ({
    signal,
  }) => listIncidentCustomerOptions(incidentId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: incidentId !== null && incidentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listIncidentCustomerOptions>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type ListIncidentCustomerOptionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listIncidentCustomerOptions>>
>;
export type ListIncidentCustomerOptionsQueryError = IncidentErrorResponse;

export function useListIncidentCustomerOptions<
  TData = Awaited<ReturnType<typeof listIncidentCustomerOptions>>,
  TError = IncidentErrorResponse,
>(
  incidentId: string,
  params: ListIncidentCustomerOptionsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listIncidentCustomerOptions>>, TError, TData>;
    request?: SecondParameter<typeof validatedIncidentFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListIncidentCustomerOptionsQueryOptions(incidentId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getChangeIncidentCustomerUrl = (incidentId: string) => {
  return `/api/v1/incidents/${incidentId}/customer`;
};

export const changeIncidentCustomer = async (
  incidentId: string,
  incidentCustomerCommand: IncidentCustomerCommand,
  options?: Parameters<typeof validatedIncidentFetch>[1],
): Promise<IncidentDetailResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedIncidentFetch<IncidentDetailResponseOutput>(
    getChangeIncidentCustomerUrl(incidentId),
    {
      ...options,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(incidentCustomerCommand),
      schema: IncidentDetailResponseOutput,
    },
  );
};

export const getChangeIncidentCustomerMutationKey = () => ['changeIncidentCustomer'] as const;

export const getChangeIncidentCustomerMutationOptions = <
  TError = IncidentErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changeIncidentCustomer>>,
    TError,
    ChangeIncidentCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedIncidentFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof changeIncidentCustomer>>,
  TError,
  ChangeIncidentCustomerMutationVariables,
  TContext
> => {
  const mutationKey = getChangeIncidentCustomerMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof changeIncidentCustomer>>,
    ChangeIncidentCustomerMutationVariables
  > = (props) => {
    const { incidentId, data } = props ?? {};

    return changeIncidentCustomer(incidentId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ChangeIncidentCustomerMutationResult = NonNullable<
  Awaited<ReturnType<typeof changeIncidentCustomer>>
>;
export type ChangeIncidentCustomerMutationBody = IncidentCustomerCommand;
export type ChangeIncidentCustomerMutationError = IncidentErrorResponse;
export type ChangeIncidentCustomerMutationVariables = {
  incidentId: string;
  data: IncidentCustomerCommand;
};

export const useChangeIncidentCustomer = <
  TError = IncidentErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changeIncidentCustomer>>,
    TError,
    ChangeIncidentCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedIncidentFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof changeIncidentCustomer>>,
  TError,
  ChangeIncidentCustomerMutationVariables,
  TContext
> => {
  return useMutation(getChangeIncidentCustomerMutationOptions(options));
};
export const getListIncidentsUrl = (params?: ListIncidentsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/incidents?${stringifiedParams}`
    : `/api/v1/incidents`;
};

export const listIncidents = async (
  params?: ListIncidentsParams,
  options?: Parameters<typeof validatedIncidentFetch>[1],
): Promise<IncidentPageResponseOutput> => {
  return validatedIncidentFetch<IncidentPageResponseOutput>(getListIncidentsUrl(params), {
    ...options,
    method: 'GET',

    schema: IncidentPageResponseOutput,
  });
};

export const getListIncidentsQueryKey = (params?: ListIncidentsParams) => {
  return [`/api/v1/incidents`, ...(params ? [params] : [])] as const;
};

export const getListIncidentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listIncidents>>,
  TError = IncidentErrorResponse,
>(
  params?: ListIncidentsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listIncidents>>, TError, TData>;
    request?: SecondParameter<typeof validatedIncidentFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListIncidentsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listIncidents>>> = ({ signal }) =>
    listIncidents(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listIncidents>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListIncidentsQueryResult = NonNullable<Awaited<ReturnType<typeof listIncidents>>>;
export type ListIncidentsQueryError = IncidentErrorResponse;

export function useListIncidents<
  TData = Awaited<ReturnType<typeof listIncidents>>,
  TError = IncidentErrorResponse,
>(
  params?: ListIncidentsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listIncidents>>, TError, TData>;
    request?: SecondParameter<typeof validatedIncidentFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListIncidentsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getGetIncidentUrl = (incidentId: string) => {
  return `/api/v1/incidents/${incidentId}`;
};

export const getIncident = async (
  incidentId: string,
  options?: Parameters<typeof validatedIncidentFetch>[1],
): Promise<IncidentDetailResponseOutput> => {
  return validatedIncidentFetch<IncidentDetailResponseOutput>(getGetIncidentUrl(incidentId), {
    ...options,
    method: 'GET',

    schema: IncidentDetailResponseOutput,
  });
};

export const getGetIncidentQueryKey = (incidentId: string) => {
  return [`/api/v1/incidents/${incidentId}`] as const;
};

export const getGetIncidentQueryOptions = <
  TData = Awaited<ReturnType<typeof getIncident>>,
  TError = IncidentErrorResponse,
>(
  incidentId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getIncident>>, TError, TData>;
    request?: SecondParameter<typeof validatedIncidentFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetIncidentQueryKey(incidentId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getIncident>>> = ({ signal }) =>
    getIncident(incidentId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: incidentId !== null && incidentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getIncident>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetIncidentQueryResult = NonNullable<Awaited<ReturnType<typeof getIncident>>>;
export type GetIncidentQueryError = IncidentErrorResponse;

export function useGetIncident<
  TData = Awaited<ReturnType<typeof getIncident>>,
  TError = IncidentErrorResponse,
>(
  incidentId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getIncident>>, TError, TData>;
    request?: SecondParameter<typeof validatedIncidentFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetIncidentQueryOptions(incidentId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getAcknowledgeIncidentUrl = (incidentId: string) => {
  return `/api/v1/incidents/${incidentId}/acknowledge`;
};

export const acknowledgeIncident = async (
  incidentId: string,
  incidentStatusCommand: IncidentStatusCommand,
  options?: Parameters<typeof validatedIncidentFetch>[1],
): Promise<IncidentDetailResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedIncidentFetch<IncidentDetailResponseOutput>(
    getAcknowledgeIncidentUrl(incidentId),
    {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(incidentStatusCommand),
      schema: IncidentDetailResponseOutput,
    },
  );
};

export const getAcknowledgeIncidentMutationKey = () => ['acknowledgeIncident'] as const;

export const getAcknowledgeIncidentMutationOptions = <
  TError = IncidentErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof acknowledgeIncident>>,
    TError,
    AcknowledgeIncidentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedIncidentFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof acknowledgeIncident>>,
  TError,
  AcknowledgeIncidentMutationVariables,
  TContext
> => {
  const mutationKey = getAcknowledgeIncidentMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof acknowledgeIncident>>,
    AcknowledgeIncidentMutationVariables
  > = (props) => {
    const { incidentId, data } = props ?? {};

    return acknowledgeIncident(incidentId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AcknowledgeIncidentMutationResult = NonNullable<
  Awaited<ReturnType<typeof acknowledgeIncident>>
>;
export type AcknowledgeIncidentMutationBody = IncidentStatusCommand;
export type AcknowledgeIncidentMutationError = IncidentErrorResponse;
export type AcknowledgeIncidentMutationVariables = {
  incidentId: string;
  data: IncidentStatusCommand;
};

export const useAcknowledgeIncident = <
  TError = IncidentErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof acknowledgeIncident>>,
    TError,
    AcknowledgeIncidentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedIncidentFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof acknowledgeIncident>>,
  TError,
  AcknowledgeIncidentMutationVariables,
  TContext
> => {
  return useMutation(getAcknowledgeIncidentMutationOptions(options));
};
export const getResolveIncidentUrl = (incidentId: string) => {
  return `/api/v1/incidents/${incidentId}/resolve`;
};

export const resolveIncident = async (
  incidentId: string,
  incidentStatusCommand: IncidentStatusCommand,
  options?: Parameters<typeof validatedIncidentFetch>[1],
): Promise<IncidentDetailResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedIncidentFetch<IncidentDetailResponseOutput>(getResolveIncidentUrl(incidentId), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(incidentStatusCommand),
    schema: IncidentDetailResponseOutput,
  });
};

export const getResolveIncidentMutationKey = () => ['resolveIncident'] as const;

export const getResolveIncidentMutationOptions = <
  TError = IncidentErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resolveIncident>>,
    TError,
    ResolveIncidentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedIncidentFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resolveIncident>>,
  TError,
  ResolveIncidentMutationVariables,
  TContext
> => {
  const mutationKey = getResolveIncidentMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resolveIncident>>,
    ResolveIncidentMutationVariables
  > = (props) => {
    const { incidentId, data } = props ?? {};

    return resolveIncident(incidentId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResolveIncidentMutationResult = NonNullable<
  Awaited<ReturnType<typeof resolveIncident>>
>;
export type ResolveIncidentMutationBody = IncidentStatusCommand;
export type ResolveIncidentMutationError = IncidentErrorResponse;
export type ResolveIncidentMutationVariables = { incidentId: string; data: IncidentStatusCommand };

export const useResolveIncident = <TError = IncidentErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resolveIncident>>,
    TError,
    ResolveIncidentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedIncidentFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof resolveIncident>>,
  TError,
  ResolveIncidentMutationVariables,
  TContext
> => {
  return useMutation(getResolveIncidentMutationOptions(options));
};
export const getChangeIncidentPriorityUrl = (incidentId: string) => {
  return `/api/v1/incidents/${incidentId}/priority`;
};

export const changeIncidentPriority = async (
  incidentId: string,
  incidentPriorityCommand: IncidentPriorityCommand,
  options?: Parameters<typeof validatedIncidentFetch>[1],
): Promise<IncidentDetailResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedIncidentFetch<IncidentDetailResponseOutput>(
    getChangeIncidentPriorityUrl(incidentId),
    {
      ...options,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(incidentPriorityCommand),
      schema: IncidentDetailResponseOutput,
    },
  );
};

export const getChangeIncidentPriorityMutationKey = () => ['changeIncidentPriority'] as const;

export const getChangeIncidentPriorityMutationOptions = <
  TError = IncidentErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changeIncidentPriority>>,
    TError,
    ChangeIncidentPriorityMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedIncidentFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof changeIncidentPriority>>,
  TError,
  ChangeIncidentPriorityMutationVariables,
  TContext
> => {
  const mutationKey = getChangeIncidentPriorityMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof changeIncidentPriority>>,
    ChangeIncidentPriorityMutationVariables
  > = (props) => {
    const { incidentId, data } = props ?? {};

    return changeIncidentPriority(incidentId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ChangeIncidentPriorityMutationResult = NonNullable<
  Awaited<ReturnType<typeof changeIncidentPriority>>
>;
export type ChangeIncidentPriorityMutationBody = IncidentPriorityCommand;
export type ChangeIncidentPriorityMutationError = IncidentErrorResponse;
export type ChangeIncidentPriorityMutationVariables = {
  incidentId: string;
  data: IncidentPriorityCommand;
};

export const useChangeIncidentPriority = <
  TError = IncidentErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changeIncidentPriority>>,
    TError,
    ChangeIncidentPriorityMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedIncidentFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof changeIncidentPriority>>,
  TError,
  ChangeIncidentPriorityMutationVariables,
  TContext
> => {
  return useMutation(getChangeIncidentPriorityMutationOptions(options));
};
export const getChangeIncidentTeamUrl = (incidentId: string) => {
  return `/api/v1/incidents/${incidentId}/team`;
};

export const changeIncidentTeam = async (
  incidentId: string,
  incidentTeamCommand: IncidentTeamCommand,
  options?: Parameters<typeof validatedIncidentFetch>[1],
): Promise<IncidentDetailResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedIncidentFetch<IncidentDetailResponseOutput>(
    getChangeIncidentTeamUrl(incidentId),
    {
      ...options,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(incidentTeamCommand),
      schema: IncidentDetailResponseOutput,
    },
  );
};

export const getChangeIncidentTeamMutationKey = () => ['changeIncidentTeam'] as const;

export const getChangeIncidentTeamMutationOptions = <
  TError = IncidentErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changeIncidentTeam>>,
    TError,
    ChangeIncidentTeamMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedIncidentFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof changeIncidentTeam>>,
  TError,
  ChangeIncidentTeamMutationVariables,
  TContext
> => {
  const mutationKey = getChangeIncidentTeamMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof changeIncidentTeam>>,
    ChangeIncidentTeamMutationVariables
  > = (props) => {
    const { incidentId, data } = props ?? {};

    return changeIncidentTeam(incidentId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ChangeIncidentTeamMutationResult = NonNullable<
  Awaited<ReturnType<typeof changeIncidentTeam>>
>;
export type ChangeIncidentTeamMutationBody = IncidentTeamCommand;
export type ChangeIncidentTeamMutationError = IncidentErrorResponse;
export type ChangeIncidentTeamMutationVariables = { incidentId: string; data: IncidentTeamCommand };

export const useChangeIncidentTeam = <
  TError = IncidentErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changeIncidentTeam>>,
    TError,
    ChangeIncidentTeamMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedIncidentFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof changeIncidentTeam>>,
  TError,
  ChangeIncidentTeamMutationVariables,
  TContext
> => {
  return useMutation(getChangeIncidentTeamMutationOptions(options));
};
